import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { bearerAuthHeaders } from "@/utils/bearerAuthHeaders";

export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_PUBLIC_BASE_URL}api`,
    prepareHeaders: (headers, { getState }) => {
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getNotifi: builder.query({
      query: (options) => {
        return {
          url: `/notifications?page=${options?.page}&limit=${options?.limit}&is_read=${options?.read}&type=${options?.type}`,
          headers: bearerAuthHeaders(options?.token),
        };
      },
    }),
    getQRCodes: builder.query({
      query: (options) => {
        return {
          url: `/qrcodes?page=${options?.page}&limit=${options?.limit}&search=${options?.search}`,
          headers: bearerAuthHeaders(options?.token),
        };
      },
    }),

    getQRAnalytics: builder.query({
      query: (options) => {
        return {
          url: `/dashboard/cards`,
          headers: bearerAuthHeaders(options?.token),
        };
      },
    }),

    getMemoryTags: builder.query({
      query: (options) => {
        return {
          url: `/memory-tags`,
          headers: bearerAuthHeaders(options?.token),
        };
      },
    }),

    GetSubscriptions: builder.query({
      query: (options) => {
        return {
          url: `/subscription/all`,
          headers: bearerAuthHeaders(options?.token),
        };
      },
    }),

    CreateQRCode: builder.mutation({
      query: ({ body, token }) => ({
        url: "/qrcodes",
        method: "POST",
        body,
        headers: bearerAuthHeaders(token),
      }),
    }),

    UpdateQRCode: builder.mutation({
      query: ({ body, token, id }) => ({
        url: `/qrcodes/${id}`,
        method: "PUT",
        body,
        headers: bearerAuthHeaders(token),
      }),
    }),

    ReadNotifications: builder.mutation({
      query: ({ body, token }) => ({
        url: `/notifications/mark-all-read`,
        method: "PATCH",
        body,
        headers: bearerAuthHeaders(token),
      }),
    }),

    ReadSingleNotification: builder.mutation({
      query: ({ body, token, id }) => ({
        url: `/notifications/${id}/read`,
        method: "PATCH",
        body,
        headers: bearerAuthHeaders(token),
      }),
    }),

    DeleteNotificaion: builder.mutation({
      query: (options) => ({
        url: `/notifications/${options?.id}`,
        method: "DELETE",
        headers: bearerAuthHeaders(options?.token),
      }),
    }),

    getQrbyId: builder.query({
      query: (options) => {
        return {
          url: `/qrcodes/${options?.id}`,
          headers: bearerAuthHeaders(options?.token),
        };
      },
    }),

    DeleteQrCode: builder.mutation({
      query: (options) => ({
        url: `/qrcodes/${options?.id}`,
        method: "DELETE",
        headers: bearerAuthHeaders(options?.token),
      }),
    }),

    ToggleNotifications: builder.mutation({
      query: ({ body, token }) => ({
        url: `/notifications/push-toggle`,
        method: "PATCH",
        body,
        headers: bearerAuthHeaders(token),
      }),
    }),

    ToggleQrCodes: builder.mutation({
      query: ({ body, id, token }) => ({
        url: `/qrcodes/${id}/toggle-status`,
        method: "PATCH",
        body,
        headers: bearerAuthHeaders(token),
      }),
    }),

    CreateMemoryTags: builder.mutation({
      query: ({ body, token }) => ({
        url: "/memory-tags",
        method: "POST",
        body,
        headers: bearerAuthHeaders(token),
      }),
    }),

    CreateScan: builder.mutation({
      query: (options: { body: unknown; token?: string; isGuest?: boolean }) => {
        const { body, token, isGuest } = options;
        const qs = isGuest ? "?type=guest" : "";
        return {
          url: `/scans/geocoded${qs}`,
          method: "POST",
          body,
          headers: { "Content-Type": "application/json", ...bearerAuthHeaders(token) },
        };
      },
    }),

    CreateSubscription: builder.mutation({
      query: ({ body, token }) => ({
        url: "/subscription/checkout-session",
        method: "POST",
        body,
        headers: bearerAuthHeaders(token),
      }),
    }),

    CancelSubscription: builder.mutation({
      query: ({ body, token, id }) => ({
        url:`/subscription/cancel/${id}`,
        method: "DELETE",
        body,
        headers: bearerAuthHeaders(token),
      }),
    }),

    VerifyScan: builder.mutation({
      query: ({ body, token, id }) => ({
        url: `/scans/verify-pin/${id}`,
        method: "POST",
        body,
        headers: bearerAuthHeaders(token),
      }),
    }),
    getScansPublicData: builder.query({
      query: (options) => {
        return {
          url: `/scans/${options?.id}`,
          headers: bearerAuthHeaders(options?.token),
        };
      },
    }),

    getUserScans: builder.query({
      query: (options) => {
        return {
          url: `/scans`,
          headers: bearerAuthHeaders(options?.token),
        };
      },
    }),
     HelpAndFeedback: builder.mutation({
      query: ({ body, token }) => ({
        url: `/help-and-feedback/contact`,
        method: "POST",
        body,
        headers: bearerAuthHeaders(token),
      }),
    }),
  }),
});

export const {
  useGetQRCodesQuery,
  useCreateQRCodeMutation,
  useUpdateQRCodeMutation,
  useGetQrbyIdQuery,
  useDeleteQrCodeMutation,
  useGetQRAnalyticsQuery,
  useGetNotifiQuery,
  useReadNotificationsMutation,
  useReadSingleNotificationMutation,
  useDeleteNotificaionMutation,
  useToggleNotificationsMutation,
  useGetMemoryTagsQuery,
  useCreateMemoryTagsMutation,
  useToggleQrCodesMutation,
  useGetScansPublicDataQuery,
  useCreateScanMutation,
  useGetUserScansQuery,
  useVerifyScanMutation,
  useGetSubscriptionsQuery,
  useCreateSubscriptionMutation,
  useCancelSubscriptionMutation,
  useHelpAndFeedbackMutation
} = userApi;
